import { createContext, useContext, useEffect, useMemo } from 'react';
import { applyBranding } from '../lib/branding.js';
import { cn } from '../lib/utils.js';

/** Bundled defaults – rendered until GET /branding has answered (and as fallback when it fails). */
export const DEFAULT_BRANDING = Object.freeze({
  company_name: 'BLIMO Immobilienverwaltung',
  short_name: 'BLIMO',
  primary_color: '#f38416',
  primary_foreground: 'dark',
  welcome_text: null,
  imprint_url: null,
  privacy_url: null,
  support_phone: null,
  support_email: null,
  logo_light_url: null,
  logo_dark_url: null,
  favicon_url: null,
  customized: false,
});

const BrandingContext = createContext(/** @type {import('../lib/branding.js').Branding} */ (DEFAULT_BRANDING));

/**
 * Provides the runtime branding to `BrandLogo`, auth layouts and app pages and applies colours/favicon/title.
 * @param {{ branding?: import('../lib/branding.js').Branding|null, title?: (b: import('../lib/branding.js').Branding) => string, children: import('react').ReactNode }} props
 *   title: document title builder (e.g. `(b) => \`${b.short_name} Portal\``)
 */
export function BrandingProvider({ branding, title, children }) {
  const value = useMemo(() => (branding ? { ...DEFAULT_BRANDING, ...withoutEmpty(branding) } : DEFAULT_BRANDING), [branding]);
  useEffect(() => {
    if (!branding) return;
    applyBranding(value);
    if (title && typeof document !== 'undefined') document.title = title(value);
  }, [branding, value, title]);
  return <BrandingContext value={value}>{children}</BrandingContext>;
}

/** @returns {import('../lib/branding.js').Branding} */
export function useBranding() {
  return useContext(BrandingContext);
}

/** Keeps the defaults for keys the server returns empty (logo URLs stay null = bundled logo). @param {Record<string, any>} b */
function withoutEmpty(b) {
  return Object.fromEntries(Object.entries(b).filter(([, v]) => v !== null && v !== undefined && v !== ''));
}

/**
 * Impressum / Datenschutz (and optionally support contact) from the branding – login pages, portal footer/profile.
 * Renders nothing when neither URL is configured.
 * @param {{ className?: string, withSupport?: boolean, linkClassName?: string }} props
 */
export function BrandingLegalLinks({ className, withSupport = false, linkClassName }) {
  const b = useBranding();
  const links = [
    b.imprint_url && { href: b.imprint_url, label: 'Impressum' },
    b.privacy_url && { href: b.privacy_url, label: 'Datenschutz' },
    withSupport && b.support_email && { href: `mailto:${b.support_email}`, label: b.support_email },
    withSupport && b.support_phone && { href: `tel:${b.support_phone.replace(/[^\d+]/g, '')}`, label: b.support_phone },
  ].filter(Boolean);
  if (links.length === 0) return null;
  return (
    <nav
      aria-label="Rechtliches"
      data-slot="branding-legal-links"
      className={cn('flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}
    >
      {links.map((l) => (
        <a
          key={l.href}
          href={l.href}
          {...(l.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className={cn('underline-offset-4 hover:text-foreground hover:underline', linkClassName)}
        >
          {l.label}
        </a>
      ))}
    </nav>
  );
}
